import { getSubjectLabels } from '@/lib/settings';
import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Eye, Save, Loader2, Search } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import LoeschenMitGrundDialog, { grundHeader } from '../../../components/ui/loeschen-mit-grund-dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { api } from '../../../lib/api';
import { toast } from 'sonner';
import { GeltungAuswahl, GeltungHinweis, useStandortOptionen, type VorlagenEbene } from './standortGeltung';

interface PdfTemplateElement {
  type: 'header' | 'text' | 'field' | 'image';
  x: number;
  y: number;
  width?: number;
  height?: number;
  fontSize?: number;
  content?: string;
  field?: string;
  src?: string;
  style?: Record<string, string>;
}

interface PdfTemplateConfig {
  elements: PdfTemplateElement[];
  pageWidth: number;
  pageHeight: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
}

interface PdfTemplate {
  id: number;
  name: string;
  description: string | null;
  isDefault: boolean;
  /** Ohne Standort gilt die Vorlage global. */
  standortId?: number | null;
  templateConfig: PdfTemplateConfig;
  createdAt: string;
  updatedAt: string;
}

const EnrollmentPdfTemplates = () => {
  const [templates, setTemplates] = useState<PdfTemplate[]>([]);
  const [zuLoeschen, setZuLoeschen] = useState<PdfTemplate | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<PdfTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogTab, setDialogTab] = useState<'edit' | 'preview'>('edit');

  // Wofür gepflegt wird: global (null) oder ein Standort — wie bei den E-Mail-Vorlagen.
  // Mit Standort stehen dessen eigene Vorlagen neben den globalen; als Standard gilt
  // dort die eigene vor der globalen.
  const standorte = useStandortOptionen();
  const [geltung, setGeltung] = useState<number | null>(null);
  const [ebene, setEbene] = useState<VorlagenEbene>('global');
  const geltungName = standorte.find((s) => s.id === geltung)?.name ?? '';
  const [standortTemplates, setStandortTemplates] = useState<PdfTemplate[]>([]);
  const [geltende, setGeltende] = useState<PdfTemplate | null>(null);

  // Form fields
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [elements, setElements] = useState<PdfTemplateElement[]>([]);
  const [pageWidth, setPageWidth] = useState(210);
  const [pageHeight, setPageHeight] = useState(297);
  const [marginTop, setMarginTop] = useState(20);
  const [marginBottom, setMarginBottom] = useState(20);
  const [marginLeft, setMarginLeft] = useState(20);
  const [marginRight, setMarginRight] = useState(20);

  // Element Editor
  const [selectedElementIndex, setSelectedElementIndex] = useState<number | null>(null);
  const [editingElement, setEditingElement] = useState<PdfTemplateElement | null>(null);

  const laden = async (standortId: number | null) => {
    const [global, eigene, gilt] = await Promise.all([
      api.get<PdfTemplate[]>('/api/enrollment-pdf-templates'),
      standortId !== null
        ? api.get<PdfTemplate[]>(`/api/enrollment-pdf-templates?standort_id=${standortId}`)
        : Promise.resolve([] as PdfTemplate[]),
      standortId !== null
        ? api.get<{ template: PdfTemplate | null }>(`/api/enrollment-pdf-templates/geltend?standort_id=${standortId}`)
        : Promise.resolve({ template: null }),
    ]);
    return { global: global || [], eigene: eigene || [], gilt: gilt?.template ?? null };
  };

  const uebernehmen = ({ global, eigene, gilt }: Awaited<ReturnType<typeof laden>>) => {
    setTemplates(global);
    setStandortTemplates(eigene);
    setGeltende(gilt);
  };

  // Nach dem Speichern neu laden: ein neuer Standard setzt andere zurück.
  const aktualisieren = async () => {
    try {
      uebernehmen(await laden(geltung));
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Templates konnten nicht geladen werden');
    }
  };

  useEffect(() => {
    let aktiv = true;
    setIsLoading(true);
    laden(geltung)
      .then((daten) => {
        if (aktiv) uebernehmen(daten);
      })
      .catch((error: unknown) => {
        if (aktiv) toast.error(error instanceof Error ? error.message : 'Templates konnten nicht geladen werden');
      })
      .finally(() => {
        if (aktiv) setIsLoading(false);
      });
    return () => {
      aktiv = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geltung]);

  const angezeigt = geltung === null ? templates : [...standortTemplates, ...templates];
  const filteredTemplates = angezeigt.filter(t => 
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateTemplate = () => {
    // Mit gewähltem Standort entsteht die neue Vorlage für ihn.
    setEbene(geltung !== null ? 'standort_neu' : 'global');
    setSelectedTemplate(null);
    setTemplateName('');
    setTemplateDescription('');
    setIsDefault(false);
    setElements([]);
    setPageWidth(210);
    setPageHeight(297);
    setMarginTop(20);
    setMarginBottom(20);
    setMarginLeft(20);
    setMarginRight(20);
    setSelectedElementIndex(null);
    setEditingElement(null);
    setDialogTab('edit');
    setIsDialogOpen(true);
  };

  const handleEditTemplate = (template: PdfTemplate) => {
    setEbene(template.standortId != null ? 'standort' : 'global');
    setSelectedTemplate(template);
    setTemplateName(template.name);
    setTemplateDescription(template.description || '');
    setIsDefault(template.isDefault);
    setElements(template.templateConfig.elements || []);
    setPageWidth(template.templateConfig.pageWidth || 210);
    setPageHeight(template.templateConfig.pageHeight || 297);
    setMarginTop(template.templateConfig.marginTop || 20);
    setMarginBottom(template.templateConfig.marginBottom || 20);
    setMarginLeft(template.templateConfig.marginLeft || 20);
    setMarginRight(template.templateConfig.marginRight || 20);
    setSelectedElementIndex(null);
    setEditingElement(null);
    setDialogTab('edit');
    setIsDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }

    setIsLoading(true);
    try {
      const config: PdfTemplateConfig = {
        elements,
        pageWidth,
        pageHeight,
        marginTop,
        marginBottom,
        marginLeft,
        marginRight,
      };

      const payload = {
        name: templateName,
        description: templateDescription || null,
        isDefault,
        templateConfig: config,
      };

      if (ebene === 'standort_neu' && geltung !== null) {
        // Eigene Vorlage des Standorts — auch als Kopie einer globalen; die globale bleibt.
        await api.post<PdfTemplate>('/api/enrollment-pdf-templates', { ...payload, standort_id: geltung });
        toast.success(`Vorlage für ${geltungName} angelegt`);
      } else if (selectedTemplate) {
        await api.put<PdfTemplate>(`/api/enrollment-pdf-templates/${selectedTemplate.id}`, payload);
        toast.success('Template wurde erfolgreich aktualisiert');
      } else {
        await api.post<PdfTemplate>('/api/enrollment-pdf-templates', payload);
        toast.success('Template wurde erfolgreich erstellt');
      }
      await aktualisieren();
      setIsDialogOpen(false);
    } catch (error: any) {
      toast.error(error?.message || 'Fehler beim Speichern des Templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTemplate = async (id: number, grund: string) => {
    const eigene = zuLoeschen?.standortId != null;
    await api.delete(`/api/enrollment-pdf-templates/${id}`, grundHeader(grund));
    setZuLoeschen(null);
    await aktualisieren();
    toast.success(eigene && geltung !== null ? `Eigene Vorlage von ${geltungName} entfernt` : 'Template wurde gelöscht');
  };

  const addElement = (type: PdfTemplateElement['type']) => {
    const newElement: PdfTemplateElement = {
      type,
      x: 10,
      y: elements.length * 30 + 10,
      width: type === 'image' ? 50 : undefined,
      height: type === 'image' ? 50 : undefined,
      fontSize: type === 'header' ? 18 : 12,
      content: type === 'header' ? 'Überschrift' : type === 'text' ? 'Text' : undefined,
      field: type === 'field' ? 'studentName' : undefined,
    };
    setElements([...elements, newElement]);
    setSelectedElementIndex(elements.length);
    setEditingElement({ ...newElement });
  };

  const updateElement = (index: number, updates: Partial<PdfTemplateElement>) => {
    const updated = [...elements];
    updated[index] = { ...updated[index], ...updates };
    setElements(updated);
    if (selectedElementIndex === index) {
      setEditingElement(updated[index]);
    }
  };

  const deleteElement = (index: number) => {
    setElements(elements.filter((_, i) => i !== index));
    if (selectedElementIndex === index) {
      setSelectedElementIndex(null);
      setEditingElement(null);
    } else if (selectedElementIndex !== null && selectedElementIndex > index) {
      setSelectedElementIndex(selectedElementIndex - 1);
    }
  };

  const availableFields = [
    { value: 'studentName', label: 'Schüler Name' },
    { value: 'studentFirstName', label: 'Schüler Vorname' },
    { value: 'studentLastName', label: 'Schüler Nachname' },
    { value: 'studentEmail', label: 'Schüler E-Mail' },
    { value: 'studentPhone', label: 'Schüler Telefon' },
    { value: 'teacherName', label: 'Lehrer Name' },
    { value: 'instrument', label: 'Instrument' },
    { value: 'fachName', label: getSubjectLabels().singular },
    { value: 'startDate', label: 'Startdatum' },
    { value: 'startTime', label: 'Startzeit' },
    { value: 'weekday', label: 'Wochentag (Nummer)' },
    { value: 'weekdayName', label: 'Wochentag (Name)' },
    { value: 'durationMin', label: 'Dauer (Minuten)' },
    { value: 'intervalWeeks', label: 'Turnus (Wochen)' },
    { value: 'roomName', label: 'Raum' },
  ];

  const getPreviewContent = (element: PdfTemplateElement): string => {
    switch (element.type) {
      case 'header':
      case 'text':
        return element.content?.replace(/{{(\w+)}}/g, (match, key) => {
          const field = availableFields.find(f => f.value === key);
          return field ? `[${field.label}]` : match;
        }) || '';
      case 'field':
        {
          const field = availableFields.find(f => f.value === element.field);
          return field ? `[${field.label}]` : '';
        }
      case 'image':
        return '[Bild]';
      default:
        return '';
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-7">PDF-Templates für Anmeldungen</h1>
          <p className="text-muted-foreground">Verwalten Sie PDF-Vorlagen für Anmeldeformulare</p>
        </div>
        <Button onClick={handleCreateTemplate}>
          <Plus className="mr-2 h-4 w-4" />Neues Template
        </Button>
      </div>

      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <GeltungAuswahl standorte={standorte} geltung={geltung} onChange={setGeltung} />
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Templates durchsuchen..." 
            className="pl-8" 
            value={searchQuery} 
            onChange={e => setSearchQuery(e.target.value)} 
          />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Templates</CardTitle>
          <CardDescription>
            {geltung === null ? (
              'Die globalen PDF-Templates — sie gelten an allen Standorten ohne eigene Vorlage.'
            ) : geltende ? (
              <>
                Standard in {geltungName}: „{geltende.name}“ (
                {geltende.standortId != null ? 'eigene Vorlage' : 'globale Vorlage'})
              </>
            ) : (
              `In ${geltungName} ist keine Standard-Vorlage festgelegt.`
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Beschreibung</TableHead>
                <TableHead>Status</TableHead>
                {geltung !== null ? <TableHead>Gilt für</TableHead> : null}
                <TableHead>Elemente</TableHead>
                <TableHead>Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTemplates.map((template) => (
                <TableRow key={template.id} className={geltung !== null && template.standortId == null ? 'text-muted-foreground' : undefined}>
                  <TableCell className="font-medium">{template.name}</TableCell>
                  <TableCell>{template.description || '-'}</TableCell>
                  <TableCell>
                    {template.isDefault ? (
                      <Badge>Standard</Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  {geltung !== null ? (
                    <TableCell>
                      {template.standortId != null ? (
                        <Badge variant="default">eigene Vorlage</Badge>
                      ) : (
                        <Badge variant="secondary">globale Vorlage</Badge>
                      )}
                    </TableCell>
                  ) : null}
                  <TableCell>{template.templateConfig.elements.length} Elemente</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => handleEditTemplate(template)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setZuLoeschen(template)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredTemplates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={geltung !== null ? 6 : 5} className="h-24 text-center">
                    Keine Templates gefunden.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedTemplate ? 'Template bearbeiten' : 'Neues Template erstellen'}
            </DialogTitle>
            <DialogDescription>
              Konfigurieren Sie das PDF-Template für Anmeldungen
            </DialogDescription>
          </DialogHeader>

          <Tabs value={dialogTab} onValueChange={(v) => setDialogTab(v as 'edit' | 'preview')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="edit">Bearbeiten</TabsTrigger>
              <TabsTrigger value="preview">Vorschau</TabsTrigger>
            </TabsList>

            <TabsContent value="edit" className="space-y-4">
              <div className="grid gap-4">
                {geltung !== null ? (
                  <GeltungHinweis
                    geltung={geltung}
                    geltungName={geltungName}
                    ebene={ebene}
                    onEigeneAnlegen={() => {
                      // Kopie der globalen als Ausgang, unter eigenem Namen.
                      setEbene('standort_neu');
                      setTemplateName(`${templateName} (${geltungName})`);
                    }}
                    globalHinweis={
                      <p>
                        Das ist eine globale Vorlage. Was du hier speicherst, ändert sie für alle Standorte, die
                        sie nutzen.
                      </p>
                    }
                  />
                ) : null}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name</Label>
                    <Input 
                      value={templateName} 
                      onChange={e => setTemplateName(e.target.value)}
                      placeholder="Template-Name"
                    />
                  </div>
                  <div className="flex items-end">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="is-default" 
                        checked={isDefault} 
                        onCheckedChange={(checked) => setIsDefault(checked === true)}
                      />
                      <Label htmlFor="is-default">
                        Als Standard-Template verwenden{geltung !== null && ebene !== 'global' ? ` in ${geltungName}` : ''}
                      </Label>
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Beschreibung</Label>
                  <Textarea 
                    value={templateDescription} 
                    onChange={e => setTemplateDescription(e.target.value)}
                    placeholder="Template-Beschreibung"
                    rows={2}
                  />
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-2">Seiteneinstellungen</h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label>Seitenbreite (mm)</Label>
                      <Input 
                        type="number" 
                        value={pageWidth} 
                        onChange={e => setPageWidth(Number(e.target.value))}
                      />
                    </div>
                    <div>
                      <Label>Seitenhöhe (mm)</Label>
                      <Input 
                        type="number" 
                        value={pageHeight} 
                        onChange={e => setPageHeight(Number(e.target.value))}
                      />
                    </div>
                    <div>
                      <Label>Ränder Oben/Unten (mm)</Label>
                      <div className="flex gap-2">
                        <Input 
                          type="number" 
                          value={marginTop} 
                          onChange={e => setMarginTop(Number(e.target.value))}
                          placeholder="Oben"
                        />
                        <Input 
                          type="number" 
                          value={marginBottom} 
                          onChange={e => setMarginBottom(Number(e.target.value))}
                          placeholder="Unten"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Ränder Links/Rechts (mm)</Label>
                      <div className="flex gap-2">
                        <Input 
                          type="number" 
                          value={marginLeft} 
                          onChange={e => setMarginLeft(Number(e.target.value))}
                          placeholder="Links"
                        />
                        <Input 
                          type="number" 
                          value={marginRight} 
                          onChange={e => setMarginRight(Number(e.target.value))}
                          placeholder="Rechts"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">Elemente</h3>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => addElement('header')}>
                        + Überschrift
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => addElement('text')}>
                        + Text
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => addElement('field')}>
                        + Feld
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => addElement('image')}>
                        + Bild
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {elements.map((element, index) => (
                      <Card 
                        key={index} 
                        className={`cursor-pointer ${selectedElementIndex === index ? 'border-primary' : ''}`}
                        onClick={() => {
                          setSelectedElementIndex(index);
                          setEditingElement({ ...element });
                        }}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="font-medium">{element.type}</div>
                              <div className="text-sm text-muted-foreground">
                                Position: {element.x}mm, {element.y}mm
                                {element.width && ` | Größe: ${element.width}mm × ${element.height || 'auto'}mm`}
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {getPreviewContent(element)}
                              </div>
                            </div>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteElement(index);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    {elements.length === 0 && (
                      <div className="text-center text-muted-foreground py-8">
                        Keine Elemente. Klicken Sie auf die Buttons oben, um Elemente hinzuzufügen.
                      </div>
                    )}
                  </div>
                </div>

                {editingElement && selectedElementIndex !== null && (
                  <div className="border-t pt-4">
                    <h3 className="font-semibold mb-2">Element bearbeiten</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>X-Position (mm)</Label>
                        <Input 
                          type="number" 
                          value={editingElement.x} 
                          onChange={e => {
                            const updated = { ...editingElement, x: Number(e.target.value) };
                            setEditingElement(updated);
                            updateElement(selectedElementIndex, updated);
                          }}
                        />
                      </div>
                      <div>
                        <Label>Y-Position (mm)</Label>
                        <Input 
                          type="number" 
                          value={editingElement.y} 
                          onChange={e => {
                            const updated = { ...editingElement, y: Number(e.target.value) };
                            setEditingElement(updated);
                            updateElement(selectedElementIndex, updated);
                          }}
                        />
                      </div>
                      {editingElement.width !== undefined && (
                        <>
                          <div>
                            <Label>Breite (mm)</Label>
                            <Input 
                              type="number" 
                              value={editingElement.width} 
                              onChange={e => {
                                const updated = { ...editingElement, width: Number(e.target.value) };
                                setEditingElement(updated);
                                updateElement(selectedElementIndex, updated);
                              }}
                            />
                          </div>
                          <div>
                            <Label>Höhe (mm)</Label>
                            <Input 
                              type="number" 
                              value={editingElement.height || ''} 
                              onChange={e => {
                                const updated = { ...editingElement, height: Number(e.target.value) };
                                setEditingElement(updated);
                                updateElement(selectedElementIndex, updated);
                              }}
                            />
                          </div>
                        </>
                      )}
                      {(editingElement.type === 'header' || editingElement.type === 'text') && (
                        <>
                          <div>
                            <Label>Schriftgröße (pt)</Label>
                            <Input 
                              type="number" 
                              value={editingElement.fontSize || 12} 
                              onChange={e => {
                                const updated = { ...editingElement, fontSize: Number(e.target.value) };
                                setEditingElement(updated);
                                updateElement(selectedElementIndex, updated);
                              }}
                            />
                          </div>
                          <div className="col-span-2">
                            <Label>{'Inhalt (Platzhalter: {{feldName}})'}</Label>
                            <Textarea 
                              value={editingElement.content || ''} 
                              onChange={e => {
                                const updated = { ...editingElement, content: e.target.value };
                                setEditingElement(updated);
                                updateElement(selectedElementIndex, updated);
                              }}
                              rows={3}
                            />
                          </div>
                        </>
                      )}
                      {editingElement.type === 'field' && (
                        <div className="col-span-2">
                          <Label>Feld</Label>
                          <Select 
                            value={editingElement.field || ''} 
                            onValueChange={value => {
                              const updated = { ...editingElement, field: value };
                              setEditingElement(updated);
                              updateElement(selectedElementIndex, updated);
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Feld auswählen" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableFields.map(field => (
                                <SelectItem key={field.value} value={field.value}>
                                  {field.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {editingElement.type === 'image' && (
                        <div className="col-span-2">
                          <Label>Bild-URL</Label>
                          <Input 
                            value={editingElement.src || ''} 
                            onChange={e => {
                              const updated = { ...editingElement, src: e.target.value };
                              setEditingElement(updated);
                              updateElement(selectedElementIndex, updated);
                            }}
                            placeholder="https://..."
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="preview">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Vorschau</CardTitle>
                  <CardDescription>
                    Diese Vorschau zeigt eine vereinfachte Darstellung des PDF-Templates
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div 
                    className="border bg-white mx-auto"
                    style={{ 
                      width: `${pageWidth}mm`, 
                      minHeight: `${pageHeight}mm`,
                      position: 'relative',
                    }}
                  >
                    {elements.map((element, index) => (
                      <div
                        key={index}
                        className="absolute border border-dashed border-gray-300 p-1"
                        style={{
                          left: `${marginLeft + element.x}mm`,
                          top: `${marginTop + element.y}mm`,
                          width: element.width ? `${element.width}mm` : 'auto',
                          height: element.height ? `${element.height}mm` : 'auto',
                          fontSize: `${element.fontSize || 12}pt`,
                        }}
                      >
                        {getPreviewContent(element) || `[${element.type}]`}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveTemplate} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />Wird gespeichert...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />Speichern
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <LoeschenMitGrundDialog
        offen={zuLoeschen !== null}
        titel={
          geltung !== null && zuLoeschen?.standortId != null
            ? `Eigene Vorlage „${zuLoeschen.name}“ von ${geltungName} entfernen?`
            : `Template „${zuLoeschen?.name ?? ''}“ löschen?`
        }
        beschreibung={
          geltung !== null && zuLoeschen?.standortId != null
            ? 'Ohne eigene Standard-Vorlage gilt an diesem Standort wieder die globale. Der Grund wird im Protokoll festgehalten.'
            : undefined
        }
        onAbbrechen={() => setZuLoeschen(null)}
        onLoeschen={async (grund) => {
          if (zuLoeschen) await handleDeleteTemplate(zuLoeschen.id, grund);
        }}
      />
    </div>
  );
};

export default EnrollmentPdfTemplates;

